import { Routes } from '@common/constants';
import { Category, Item } from 'shared-types/Common';
import { Product } from 'shared-types/Product';
import { SpaEntityType } from 'shared-types/Spa';
import { SpaPackageCategoryType } from 'shared-types/SpaPackage';
import type { SpaListItem } from 'voyager';

export type CategoryItemTypeLabels = {
  spaDay: string;
  treatment: string;
  facility: string;
  product: string;
};

export type CategoryItemRoute = { route: Routes; id: string };

export type CategoryItemsResult = {
  items: SpaListItem[];
  routesById: Record<string, CategoryItemRoute>;
};

function buildPrice(itemPrice: { amount?: number; currencyCode?: string } | null | undefined) {
  if (!itemPrice || itemPrice.amount == null || !itemPrice.currencyCode) return null;
  return { amount: itemPrice.amount, currencyCode: itemPrice.currencyCode };
}

function buildId(prefix: string, id: string | number): string {
  return `${prefix}-${id}`;
}

type BuildCategoryItemsArgs = {
  category: Category;
  labels: CategoryItemTypeLabels;
  fallbackImageUrl?: string;
};

export function buildCategoryItems({
  category,
  labels,
  fallbackImageUrl,
}: BuildCategoryItemsArgs): CategoryItemsResult {
  const items: SpaListItem[] = [];
  const routesById: Record<string, CategoryItemRoute> = {};

  const push = (item: SpaListItem, route: Routes, rawId: string | number) => {
    items.push(item);
    routesById[item.id] = { route, id: String(rawId) };
  };

  (category.spaPackages ?? []).forEach((sp: SpaPackageCategoryType) => {
    push(
      {
        id: buildId('spaDay', sp.id),
        name: sp.name,
        imageUrl: sp.image?.url || fallbackImageUrl,
        price: buildPrice(sp.price),
        duration: labels.spaDay,
      },
      Routes.SPA_DAY_DETAIL,
      sp.id
    );
  });

  (category.treatments ?? []).forEach((tr: SpaEntityType) => {
    push(
      {
        id: buildId('treatment', tr.id),
        name: tr.name,
        imageUrl: tr.image?.url || fallbackImageUrl,
        price: buildPrice((tr as unknown as Item).price),
        duration: labels.treatment,
      },
      Routes.SPA_TREATMENT_DETAIL,
      tr.id
    );
  });

  (category.facilities ?? []).forEach((fa: SpaEntityType) => {
    push(
      {
        id: buildId('facility', fa.id),
        name: fa.name,
        imageUrl: fa.image?.url || fallbackImageUrl,
        price: buildPrice((fa as unknown as Item).price),
        duration: labels.facility,
      },
      Routes.FACILITY_DETAIL,
      fa.id
    );
  });

  (category.products ?? []).forEach((pr: Product) => {
    const image = pr.image ?? pr.imageGallery?.[0];
    push(
      {
        id: buildId('product', pr.id),
        name: pr.name,
        imageUrl: image?.url || fallbackImageUrl,
        price: buildPrice((pr as Item).price),
        duration: labels.product,
      },
      Routes.PRODUCT_DETAIL,
      pr.id
    );
  });

  return { items, routesById };
}
